import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { useStaticQuery, graphql, Link } from "gatsby";
import KeywordSearch from "./KeywordSearch";
import { blocksToText } from "../../../utils";
import { SearchResultsArrowIcon } from "../../../assets/svgs";

const popularSearchTerms = [
  "Cardiology",
  "Whole Genome Sequencing",
  "Biobanking",
  "Prenatal",
  "DNA",
  "Non-invasive prenatal screening"
];

// Pages that aren't backed by a Sanity document, so they can't be pulled in
// via GraphQL like the CMS-driven services below. Their descriptions come from
// the matching markdown file's frontmatter instead - `dataKey` matches that
// file's `pageTitle` (falling back to `slug`), since slug alone isn't unique
// for the two Tumour Based CGP pages.
const staticServicePages = [
  {
    title: "Biobanking and Sample Management Services",
    path: "/services/biobanking-and-sample-management",
    dataKey: "biobanking-and-sample-management",
  },
  {
    title: "PrenatalSEQ",
    path: "/services/clinical/prenatal-seq",
    dataKey: "prenatal-seq",
  },
  {
    title: "Cascade Testing",
    path: "/services/clinical/cascade-testing",
    dataKey: "cascade-testing",
  },
  {
    title: "Genetic Counselling",
    path: "/services/clinical/genetic-counselling",
    dataKey: null,
  },
  {
    title: "Tumour Based Comprehensive Genomic Profiling (Clinical)",
    path: "/services/clinical/tumour-based-comprehensive-genomic-profiling",
    dataKey: "clinical-tumour-based",
  },
  {
    title: "Tumour Based Comprehensive Genomic Profiling (Biopharma / Research)",
    path: "/services/biopharma-and-research/tumour-based-comprehensive-genomic-profiling",
    dataKey: "biopharma-tumour-based",
  },
  {
    title: "Companion Diagnostics / Pharmacogenomic Services",
    path: "/services/biopharma-and-research/companion-diagnostics-pharmacogenomic-services",
    dataKey: "companion-diagnostics-pharmacogenomic-services",
  },
];

// Frontmatter descriptions/whatIs fields contain HTML markup - strip it so
// tag names don't pollute the searchable text.
const stripHtml = (html = "") => html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

function SearchBarMenu({ windowWidth, setShowSearchMenu }) {
  const [dataset, setDataset] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const staticQueryResults = useStaticQuery(graphql`
    query {
      biopharmaServices: allSanityBiopharmaServices {
        nodes {
          title
          leadingParagraph
          slug {
            current
          }
        }
      }
      clinicalServices: allSanityClinicalServices {
        nodes {
          title
          slug {
            current
          }
          serviceCategory {
            slug {
              current
            }
          }
          serviceSubCategory {
            slug {
              current
            }
          }
          about {
            _rawReferenceBlock
          }
        }
      }
      clinicalServiceCategories: allSanityClinicalServiceCategories {
        nodes {
          title
          slug {
            current
          }
        }
      }
      clinicalServiceSubCategories: allSanityClinicalServiceSubCategories {
        nodes {
          title
          slug {
            current
          }
          serviceCategory {
            slug {
              current
            }
          }
        }
      }
      staticServiceMarkdown: allMarkdownRemark {
        nodes {
          frontmatter {
            slug
            pageTitle
            description
            whatIs
          }
        }
      }
    }
  `);

  useEffect(() => {
    const {
      biopharmaServices,
      clinicalServices,
      clinicalServiceCategories,
      clinicalServiceSubCategories,
      staticServiceMarkdown,
    } = staticQueryResults;

    const markdownByDataKey = {};
    staticServiceMarkdown.nodes.forEach(({ frontmatter }) => {
      const key = frontmatter.pageTitle || frontmatter.slug;
      if (!key) return;
      markdownByDataKey[key] = stripHtml(
        `${frontmatter.description || ""} ${frontmatter.whatIs || ""}`,
      );
    });

    const _entries = staticServicePages.map((page) => {
      const markdownText = page.dataKey ? markdownByDataKey[page.dataKey] : "";
      return {
        ...page,
        searchableText: `${page.title} ${markdownText || ""}`.trim(),
      };
    });

    if (biopharmaServices.nodes.length) {
      biopharmaServices.nodes.forEach((service) => {
        if (!service.slug?.current) return;

        const path = `/services/biopharma-and-research/${service.slug.current}`;

        const entry = {
          title: service.title,
          path,
          searchableText: `${service.title} ${service.leadingParagraph || ""}`,
        };

        _entries.push(entry);
      });
    }

    if (clinicalServices.nodes.length) {
      clinicalServices.nodes.forEach((service) => {
        const { title, slug, serviceCategory, serviceSubCategory, about } =
          service;
        // Clinical service pages are only built when a category is assigned
        // (see gatsby-node.js), so services without one have no page to link to.
        if (!slug?.current || !serviceCategory?.slug?.current) return;

        const path = serviceSubCategory?.slug?.current
          ? `/services/clinical/${serviceCategory.slug.current}/${serviceSubCategory.slug.current}/${slug.current}`
          : `/services/clinical/${serviceCategory.slug.current}/${slug.current}`;

        const aboutText = blocksToText(about?._rawReferenceBlock);

        const entry = {
          title,
          path,
          searchableText: `${title} ${aboutText}`,
        };

        _entries.push(entry);
      });
    }

    if (clinicalServiceCategories.nodes.length) {
      clinicalServiceCategories.nodes.forEach((category) => {
        if (!category.slug?.current) return;

        const entry = {
          title: category.title,
          path: `/services/clinical/${category.slug.current}`,
          searchableText: category.title,
        };

        _entries.push(entry);
      });
    }

    if (clinicalServiceSubCategories.nodes.length) {
      clinicalServiceSubCategories.nodes.forEach((subCategory) => {
        if (
          !subCategory.slug?.current ||
          !subCategory.serviceCategory?.slug?.current
        )
          return;

        const entry = {
          title: subCategory.title,
          path: `/services/clinical/${subCategory.serviceCategory.slug.current}/${subCategory.slug.current}`,
          searchableText: subCategory.title,
        };

        _entries.push(entry);
      });
    }

    setDataset(_entries);
  }, [staticQueryResults]);

  useEffect(() => {
    if (dataset?.length) {
      if (searchQuery !== "") {
        const _searchResults = dataset.filter((entry) =>
          entry.searchableText
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
        );
        setSearchResults(_searchResults);
      } else {
        setSearchResults([]);
      }
    }
  }, [searchQuery, dataset]);

  const handleSearchInput = ({ target }) => {
    setSearchQuery(target.value);
  };

  const handleTermClick = (term) => {
    setSearchQuery(term);
  };

  return (
    <Container
      custom={windowWidth}
      onMouseLeave={() => setShowSearchMenu(false)}
    >
      <AnimatePresence>
        <Card>
          <SearchInputWrapper>
            <KeywordSearch
              searchQuery={searchQuery}
              handleSearchInput={handleSearchInput}
              placeholder="Search for..."
              autoFocus
            />
          </SearchInputWrapper>
          <ResultsPanel>
            <Label>{searchQuery ? "Search Results" : "Popular Search Terms"}</Label>
            {searchQuery ? (
              <ResultsList>
                {searchResults.map((result) => (
                  <Link
                    key={result.path}
                    to={result.path}
                    tabIndex={0}
                    onClick={() => setShowSearchMenu(false)}
                  >
                    <SearchResultsArrowIcon width="12px" />
                    {result.title}
                  </Link>
                ))}
                {!searchResults.length && (
                  <NoResults>No results found.</NoResults>
                )}
              </ResultsList>
            ) : (
              <TermsList>
                {popularSearchTerms.map((term) => (
                  <TermPill
                    key={term}
                    type="button"
                    onClick={() => handleTermClick(term)}
                  >
                    {term}
                  </TermPill>
                ))}
              </TermsList>
            )}
          </ResultsPanel>
        </Card>
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.div`
  left: 50%;
  margin-top: 0.75rem;
  max-width: 1400px;
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
  width: calc(100% - 3rem);
  z-index: 5;
`;

const Card = styled(motion.div).attrs({
  initial: {
    opacity: 0,
    y: -12,
  },
  animate: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: -12,
  },
  transition: {
    type: "tween",
    ease: "easeInOut",
    duration: 0.3,
  },
})`
  background-color: #fff;
  border-radius: 1.5rem;
  box-shadow: 0px 12px 30px rgba(217, 217, 217, 0.3);
  padding: 2.5rem;

  @media (width >= 768px) {
    padding: 2.5rem 3rem;
  }
`;

const SearchInputWrapper = styled.div`
  caret-color: var(--genseq-purple);

  svg {
    display: none;
  }

  input {
    background-color: transparent;
    border-bottom: 1px solid rgb(6 28 87 / 15%);
    height: auto;
    margin-bottom: 0;
    padding: 0 0 1rem 0.25rem;

    &::placeholder {
      color: var(--genseq-blue);
      font-size: 1.25rem;
      font-style: italic;
      font-weight: 300;
      opacity: 0.55;
      text-transform: none;
    }
  }
`;

const ResultsPanel = styled.div`
  margin-top: 1.5rem;
`;

const Label = styled.p`
  color: var(--genseq-purple);
  font-size: 0.9375rem;
  font-weight: 500;
  margin-bottom: 1rem;
`;

const TermsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const TermPill = styled.button`
  background-color: rgb(100 95 230 / 12%);
  border-radius: 2rem;
  color: var(--genseq-blue);
  cursor: pointer;
  font-size: 0.9375rem;
  font-weight: 700;
  padding: 0.625rem 1.25rem;
  transition: background-color 0.2s ease-in-out;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    background-color: rgb(100 95 230 / 22%);
  }
`;

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-height: 320px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 0.5rem;
  }

  ::-webkit-scrollbar-track {
    border-radius: 0.625rem;
    box-shadow: inset 0 0 0.31rem var(--genseq-blue);
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--genseq-purple);
    border-radius: 0.625rem;
  }

  a {
    align-items: center;
    border-radius: 2rem;
    color: var(--genseq-blue);
    display: flex;
    font-size: 1rem;
    font-weight: 700;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    text-decoration: none;
    transition: background-color 0.2s ease-in-out;

    &:hover,
    &:focus-visible {
      background-color: rgb(6 28 87 / 6%);
    }
  }

  svg {
    flex-shrink: 0;
  }
`;

const NoResults = styled.p`
  color: var(--genseq-blue);
  font-size: 1rem;
  font-weight: 400;
  opacity: 0.6;
  padding: 0.75rem 1rem;
`;

export default SearchBarMenu;
